/**
 * §18.7 — the payroll register: every pay run in a period, per employee and in
 * total (payroll stage 4).
 *
 * THE SUMS ARE THE SERVER'S. GET /payroll/register adds each run's stored
 * figures per employee and in total, so this screen is not a second place that
 * adds them up. It combines stored figures only where one column here shows
 * two of them: Medicare withheld with Additional Medicare, in the year to date
 * too, and the employer's four taxes.
 *
 * WHAT IS NOT HERE, SAID RATHER THAN LEFT TO BE NOTICED. Voided and reversed
 * runs paid nothing that stands. Pay entered by hand before the first run --
 * this business's first half of 2026 -- is in each employee's opening totals,
 * not in any run, so it has no rows; since 2026-09-23 it is named above the
 * table whenever the period meets it, because a register of the first half
 * read as if nothing had been paid. And a run whose entry is still a draft is
 * shown and counted but named, because the forms are drawn from posted runs
 * (§18.7).
 *
 * THE YEAR TO DATE THROUGH THE PERIOD'S END is under the table, the server's
 * (apps/api/src/services/year-to-date.ts): where the hand-entered pay is
 * counted, with the register's columns but the employer's taxes, which the
 * hand-entered totals never recorded.
 *
 * The period is the Reports screen's: From and To, kept in the address so a
 * view of the register can be sent to someone. Without them the server
 * answers the quarter the business is in.
 *
 * THE BOXES SHOW THE PERIOD THE TABLE IS FOR. An emptied or half-typed date is
 * never sent: the server reads a missing date as its own default, so the table
 * changed to July to September under a From box reading "01" (reported
 * 2026-09-23). A date being typed stays on screen until it is whole, and a box
 * left half-typed goes back to the date in use.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { InfoNote } from '../components/InfoNote.js';
import { ApiError, api, type PayrollRegister, type RegisterAmounts, type YearToDateFigures } from '../api.js';
import { formatCents } from '../lib/money.js';

/** Medicare withheld, Additional Medicare included: one column on this screen. */
function medicare(amounts: RegisterAmounts): number {
  return amounts.medicareEmployeeCents + amounts.additionalMedicareEmployeeCents;
}

/** The employer's share of Social Security and Medicare, FUTA and Texas unemployment. */
function employerTaxes(amounts: RegisterAmounts): number {
  return (
    amounts.socialSecurityEmployerCents +
    amounts.medicareEmployerCents +
    amounts.futaEmployerCents +
    amounts.texasUnemploymentEmployerCents
  );
}

/** The figure columns, in order, shared by every row, subtotal and total. */
function Figures({ amounts }: { amounts: RegisterAmounts }) {
  return (
    <>
      <td className="num">{formatCents(amounts.grossPayCents)}</td>
      <td className="num">{formatCents(amounts.federalIncomeTaxWithheldCents)}</td>
      <td className="num">{formatCents(amounts.socialSecurityEmployeeCents)}</td>
      <td className="num">{formatCents(medicare(amounts))}</td>
      <td className="num">{formatCents(amounts.netPayCents)}</td>
      <td className="num">{formatCents(employerTaxes(amounts))}</td>
    </>
  );
}

const FIGURE_HEADINGS = ['Gross', 'Income Tax', 'Social Security', 'Medicare', 'Net Pay', 'Employer Taxes'];

/** The year to date's columns: the register's, but the employer's taxes, which opening totals do not record. */
function YearFigures({ figures }: { figures: YearToDateFigures }) {
  return (
    <>
      <td className="num">{formatCents(figures.grossPayCents)}</td>
      <td className="num">{formatCents(figures.incomeTaxWithheldCents)}</td>
      <td className="num">{formatCents(figures.socialSecurityTaxCents)}</td>
      <td className="num">{formatCents(figures.medicareTaxCents + figures.additionalMedicareTaxCents)}</td>
      <td className="num">{formatCents(figures.netPayCents)}</td>
    </>
  );
}

const YEAR_HEADINGS = FIGURE_HEADINGS.slice(0, 5);

export function RegisterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const fromParam = searchParams.get('from') ?? undefined;
  const toParam = searchParams.get('to') ?? undefined;
  const [register, setRegister] = useState<PayrollRegister | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** A date still being typed, shown as typed and not yet sent. */
  const [typing, setTyping] = useState<{ from?: string; to?: string }>({});

  useEffect(() => {
    let live = true;
    api
      .getPayrollRegister(fromParam, toParam)
      .then((result) => {
        if (!live) return;
        setRegister(result.register);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (live) setError(caught instanceof ApiError ? caught.message : 'Could not read the register.');
      });
    return () => {
      live = false;
    };
  }, [fromParam, toParam]);

  // The dates in use: an empty one in the address is the server's default, so the server's is shown.
  const shownFrom = fromParam || register?.from || '';
  const shownTo = toParam || register?.to || '';

  function setPeriod(next: { from?: string; to?: string }) {
    const params = new URLSearchParams(searchParams);
    // Both written, so a link to this view carries the period it was read at.
    params.set('from', next.from ?? shownFrom);
    params.set('to', next.to ?? shownTo);
    setSearchParams(params, { replace: true });
  }

  function edit(field: 'from' | 'to', value: string) {
    if (value === '') {
      setTyping((current) => ({ ...current, [field]: value }));
      return;
    }
    setTyping((current) => ({ ...current, [field]: undefined }));
    setPeriod({ [field]: value });
  }

  const drafts = register?.runs.filter((run) => run.entry?.status !== 'posted').length ?? 0;

  return (
    <section className="register">
      <p>
        <Link to="/payroll">Back to Payroll</Link>
      </p>
      <h1>
        Payroll Register{' '}
        <InfoNote label="What Is in the Register">
          Every pay run between these dates that was not voided or reversed, as the run calculated it. Medicare includes
          Additional Medicare withheld; employer taxes are the employer's Social Security and Medicare, FUTA and Texas
          unemployment tax. Pay entered by hand before the first run is in each employee's opening totals, not in the
          table: it is named when the period includes it, and counted in the year to date below.
        </InfoNote>
      </h1>

      <div className="row report-period">
        <label>
          From{' '}
          <input
            type="date"
            value={typing.from ?? shownFrom}
            onChange={(event) => edit('from', event.target.value)}
            onBlur={() => setTyping((current) => ({ ...current, from: undefined }))}
          />
        </label>
        <label>
          To{' '}
          <input
            type="date"
            value={typing.to ?? shownTo}
            onChange={(event) => edit('to', event.target.value)}
            onBlur={() => setTyping((current) => ({ ...current, to: undefined }))}
          />
        </label>
      </div>

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {register === null && error === null && <p className="muted">Loading…</p>}

      {register !== null && error === null && (
        <>
          {register.handEntered.length > 0 && <HandEntered register={register} />}
          {register.runs.length === 0 ? (
            <p className="muted">
              No pay runs between {register.from} and {register.to}.
            </p>
          ) : (
            <>
              {drafts > 0 && (
                <p className="notice notice-warn">
                  {drafts === 1 ? "One run's entry is" : `${drafts} runs' entries are`} still a draft. The forms are drawn
                  from posted runs, so post {drafts === 1 ? 'it' : 'them'} before relying on these figures for a return.
                </p>
              )}
              <div className="table-scroll">
                <table className="lines" aria-label="Pay Runs">
                  <thead>
                    <tr>
                      <th>Pay Date</th>
                      <th>Employee</th>
                      {/* Before the figures: a left-aligned badge against a right-aligned amount reads as one cell. */}
                      <th>Entry</th>
                      {FIGURE_HEADINGS.map((heading) => (
                        <th key={heading} className="num">
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {register.runs.map((run) => (
                      <tr key={run.runId}>
                        <td className="date-cell">
                          <Link to={`/payroll/runs/${run.runId}`}>{run.payDate}</Link>
                        </td>
                        <td>{run.employeeName}</td>
                        <td>
                          {run.entry === null ? (
                            <span className="muted">None</span>
                          ) : (
                            <span className={`tag ${run.entry.status === 'posted' ? 'tag-known' : 'tag-due'}`}>
                              {run.entry.status === 'posted' ? 'Posted' : 'Draft'}
                            </span>
                          )}
                        </td>
                        <Figures amounts={run} />
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row" colSpan={3}>
                        Total, {register.totals.runCount} {register.totals.runCount === 1 ? 'run' : 'runs'}
                      </th>
                      <Figures amounts={register.totals} />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Only where there is more than one person: with one, it would repeat the total. */}
              {register.employees.length > 1 && (
                <section className="settings-section" aria-labelledby="register-employees">
                  <h2 id="register-employees">By Employee</h2>
                  <div className="table-scroll">
                    <table className="lines" aria-label="By Employee">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th className="num">Runs</th>
                          {FIGURE_HEADINGS.map((heading) => (
                            <th key={heading} className="num">
                              {heading}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {register.employees.map((employee) => (
                          <tr key={employee.employeeId}>
                            <td>{employee.employeeName}</td>
                            <td className="num">{employee.totals.runCount}</td>
                            <Figures amounts={employee.totals} />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </>
          )}
          <YearToDate register={register} />
        </>
      )}
    </section>
  );
}

/** Pay entered by hand that the period meets: not a run, so no rows. */
function HandEntered({ register }: { register: PayrollRegister }) {
  const ytd = register.yearToDate;
  // Only where it is so: an opening total of another year, or one the year to date cannot divide, is not in it.
  const counted = register.handEntered.every(
    (opening) => ytd !== null && opening.year === ytd.year && ytd.through !== null && opening.throughDate <= ytd.through,
  );
  const several = register.handEntered.length > 1;
  return (
    <div className="notice">
      Pay entered by hand before the first pay run is not a run, so it has no rows here:
      <ul className="missed-list">
        {register.handEntered.map((opening) => (
          <li key={`${opening.employeeId}-${opening.year}`}>
            {opening.employeeName}, {opening.year}-01-01 to {opening.throughDate}
          </li>
        ))}
      </ul>
      {counted && `The year to date below counts ${several ? 'them' : 'it'}.`}
    </div>
  );
}

/** The year through the period's end, the hand-entered pay counted where it can be. */
function YearToDate({ register }: { register: PayrollRegister }) {
  const ytd = register.yearToDate;
  const year = ytd?.year ?? Number(register.to.slice(0, 4));
  const drafts = ytd?.draftRuns.length ?? 0;
  return (
    <section className="settings-section" aria-labelledby="register-ytd">
      <h2 id="register-ytd">Year to Date, {year}</h2>
      {ytd === null ? (
        <p className="muted">{register.yearToDateRefusal}</p>
      ) : (
        <>
          <p className="muted">
            Through {ytd.through}: pay entered by hand before the first pay run, and every posted run. The employer&apos;s
            taxes are not in the hand-entered totals, so they are not here.{' '}
            <Link to={`/payroll/year-to-date?year=${ytd.year}`}>Every Figure for {ytd.year}</Link>
          </p>
          {ytd.employees.length === 0 ? (
            <p className="muted">
              Nothing is counted for {ytd.year} through {ytd.through}.
            </p>
          ) : (
            <div className="table-scroll">
              <table className="lines" aria-label="Year to Date">
                <thead>
                  <tr>
                    <th>Employee</th>
                    {YEAR_HEADINGS.map((heading) => (
                      <th key={heading} className="num">
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ytd.employees.map((employee) => (
                    <tr key={employee.employeeId}>
                      <td>{employee.employeeName}</td>
                      <YearFigures figures={employee.figures} />
                    </tr>
                  ))}
                </tbody>
                {/* With one employee a total would repeat the row above it. */}
                {ytd.employees.length > 1 && (
                  <tfoot>
                    <tr>
                      <th scope="row">Total</th>
                      <YearFigures figures={ytd.totals} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
          {ytd.openingNotSplit.map((opening) => (
            <p key={opening.employeeId} className="muted">
              {opening.employeeName}&apos;s pay through {opening.throughDate} was entered by hand as one total and cannot
              be divided at {ytd.through}, so it is not in this year to date.
            </p>
          ))}
          {drafts > 0 && (
            <p className="muted">
              {drafts === 1 ? 'One run whose entry is a draft is' : `${drafts} runs whose entries are drafts are`} not
              counted here.
            </p>
          )}
        </>
      )}
    </section>
  );
}
